import { useState, MouseEvent, ChangeEvent } from 'react'
import { TransformedCompanies } from '@features/company_management/hooks'

type DeleteMode = 'single' | 'bulk' | null

interface UseCompanyManagementTableHandlersProps {
  onEditClick: (companyId: string) => void
  onDeleteCompany: (companyId: string) => void
  onBulkDeleteCompanies: (seriesLabelIds: Array<string>) => void
  companies: TransformedCompanies | undefined
  canHardDelete: boolean
}

export default function useCompanyManagementTableHandlers({
  onEditClick,
  onDeleteCompany,
  onBulkDeleteCompanies,
  companies,
  canHardDelete,
}: UseCompanyManagementTableHandlersProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState<DeleteMode>(null)

  const handleEditClick = (e: MouseEvent<HTMLElement>, companyId: string) => {
    e.preventDefault()
    e.stopPropagation()
    onEditClick(companyId)
  }

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(companies?.items.map((row) => row.id) ?? [])
    } else {
      setSelected([])
    }
  }
  const handleClick = (_event: MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id)
    const newSelected = selectedIndex === -1 ? [...selected, id] : selected.filter((selectedId) => selectedId !== id)
    setSelected(newSelected)
  }

  const handleOpenMenu = (event: MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setSelectedCompanyId(id)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleDeleteCompany = () => {
    if (!canHardDelete) {
      return
    }
    if (selectedCompanyId) {
      setMenuAnchorEl(null)
      setDeleteMode('single')
      setConfirmDialogOpen(true)
    }
  }

  const handleBulkDeleteCompanies = () => {
    if (!canHardDelete) {
      return
    }
    if (selected.length) {
      setDeleteMode('bulk')
      setConfirmDialogOpen(true)
    }
  }

  const resetDeleteConfirmation = () => {
    setConfirmDialogOpen(false)
    setDeleteMode(null)
    setSelectedCompanyId(null)
    setMenuAnchorEl(null)
  }

  const handleConfirmDelete = () => {
    if (!canHardDelete) {
      return
    }
    if (deleteMode === 'single' && selectedCompanyId) {
      onDeleteCompany(selectedCompanyId)
    } else if (deleteMode === 'bulk' && selected.length) {
      onBulkDeleteCompanies(selected)
      setSelected([])
    }
    resetDeleteConfirmation()
  }

  const handleCancelDelete = () => {
    resetDeleteConfirmation()
  }

  const isPluralDelete = deleteMode === 'bulk' && selected.length > 1
  const confirmDialogTitle = isPluralDelete ? 'Delete Companies' : 'Delete Company'
  const confirmDialogDescription = isPluralDelete
    ? 'This will permanently delete the selected Companies and cannot be undone.'
    : 'This will permanently delete the Company and cannot be undone.'

  return {
    selected,
    menuAnchorEl,
    selectedCompanyId,
    confirmDialogOpen,
    confirmDialogTitle,
    confirmDialogDescription,
    handleEditClick,
    handleSelectAllClick,
    handleClick,
    handleOpenMenu,
    handleCloseMenu,
    handleDeleteCompany,
    handleBulkDeleteCompanies,
    handleConfirmDelete,
    handleCancelDelete,
  }
}
